<template>
  <div class="w-100 px-2" ref="containerRef">
    <flex-layout v-if="!props.noLabel" class="w-100 pe-2" :class="{'label-border mb-2': hasLabelBorder}">
      <base-text :color="labelColor">{{ props.label }}</base-text>
      <required-tag-text v-if="props.requiredTag || props.primaryRequiredTag" :color="props.primaryRequiredTag? 'primary' : 'error'">必須</required-tag-text>
    </flex-layout>
    <div v-if="hasAppend" class="d-flex px-0 align-items-center">
      <div class="input-container" ref="inputRef" @click="onClick" @keyup.enter="onClick">
        <input
          class="flex-grow-1"
          :class="{'error': !!error, 'invalidField': !!validate(rules, props.modelValue), 'placeholder-style': isPlaceholder }"
          :value="selectedText"
          readonly
          :disabled="props.disabled"
          :placeholder="props.placeholder"
          type="text"
          @click.prevent
        >
        <base-icon
          v-if="props.clearable"
          class="clickable"
          width="20px"
          height="20px"
          :stroke="props.disabled ? 'gray': ''"
          name="cross-sign"
          @click.stop="onSelect(returnObject ? {} : undefined)"
        />
        <base-icon
          v-if="isPlaceholder"
          class="clickable"
          width="20px"
          height="20px"
          :stroke="props.disabled ? 'gray': ''"
          name="chevron-down"
        />
      </div>
      <slot name="append" />
    </div>
    <div v-else class="input-container" ref="inputRef" @click="onClick" @keyup.enter="onClick">
      <input
        class="flex-grow-1"
        :class="{'error': !!error, 'invalidField': !!validate(rules, props.modelValue),  'placeholder-style': isPlaceholder }"
        :value="selectedText"
        readonly
        :disabled="props.disabled"
        :placeholder="props.placeholder"
        type="text"
        @click.prevent
      >
      <base-icon
        v-if="(props.clearable || !isPlaceholder) && !alwaysChevron "
        class="clickable"
        width="20px"
        height="20px"
        :stroke="props.disabled ? 'gray': ''"
        name="cross-sign"
        @click.stop="onSelect(returnObject ? {} : undefined)"/>
      <base-icon
        v-if="(isPlaceholder || !selectedItems || alwaysChevron) && !props.clearable "
        class="clickable"
        width="20px"
        height="20px"
        block
        :stroke="props.disabled ? 'gray': ''"
        name="chevron-down"/>
    </div>
    <base-menu v-if="breakpoint.mdAndUp && !disabled" :activator="inputRef">
      <div class="menu-content p-0">
        <base-text class="menu-title" bold>{{ selectedText || props.placeholder }}</base-text>
        <div class="menu-list">
          <div class="menu-item" v-for="(item, i) in props.items" :key="i" @click="onSelect(item)"
               @keyup.enter="onSelect(item)">
            <i class="bi bi-chevron-right"></i>
            <base-text class="w-100 pt-2" justify="center">{{ item[props.itemText] }}</base-text>
          </div>
        </div>
      </div>
    </base-menu>
    <sliding-up-layout v-else-if="!disabled" :activator="inputRef">
      <div class="menu-content p-0">
        <base-text class="menu-title" bold>{{ selectedText || props.placeholder }}</base-text>
        <div class="menu-list">
          <div class="menu-item" v-for="(item, i) in props.items" :key="i" @click="onSelect(item)"
               @keyup.enter="onSelect(item)">
            <i class="bi bi-chevron-right"></i>
            <base-text class="w-100 pt-2" justify="center">{{ item[props.itemText] }}</base-text>
          </div>
        </div>
      </div>
    </sliding-up-layout>
    <base-text v-if="!noError" color="error" size="xs">{{ error }}</base-text>
  </div>
</template>

<script lang="ts" setup generic="T">
import {computed, defineEmits, defineProps, ref, toRef} from 'vue';
import BaseText from '@/components/Texts/BaseText.vue';
import FlexLayout from '@/components/Layouts/FlexLayout.vue';
import BaseMenu from '@/components/Layouts/BaseMenu.vue';
import {useBreakpoint} from '@/composable/ui/useScreen';
import SlidingUpLayout from '@/components/Layouts/SlidingUpLayout.vue';
import RequiredTagText from '@/components/Texts/RequiredTagText.vue';
import BaseIcon from '@/components/Images/BaseIcon.vue';
import {ValidatorRule} from '@/composable/ui/useForm';
import useFormField from '@/composable/ui/useFormField';
import {useValidator} from "@/composable/ui/useValidator";
const { validate } = useValidator();

const props = defineProps(
  {
    label: String,
    noLabel: Boolean,
    modelValue: [String, Number, Object, Boolean],
    placeholder: String,
    rules: {
      type: [Array as () => ValidatorRule<string>, Object as () => ValidatorRule<string>],
      default: () => [],
    },
    items: {
      type: Array as () => { [key: string]: any }[],
      default: () => [],
    },
    disabled: Boolean,
    clearable: Boolean,
    returnObject: Boolean,
    itemValue: {type: String, default: 'value'},
    itemText: {type: String, default: 'text'},
    noError: {type: Boolean, default: false},
    requiredTag: {
      type: Boolean,
      default: false,
    },
    labelColor: {
      type: String,
      default: 'primary',
    },
    hasLabelBorder: {
      type: Boolean,
      default: false,
    },
    hasAppend: {
      type: Boolean,
    },
    dependencyField: {
      type: [String, Number],
    },
    primaryRequiredTag: {
      type: Boolean,
      default: false,
    },
    isOptional: {
      type: Boolean,
      default: false,
    },
    alwaysChevron: {
      type: Boolean,
      default: false,
    }
  },
);
const emit = defineEmits(['update:modelValue']);

const dependencyField = computed(() => props.dependencyField)


const openMenu = ref(false);
const inputRef = ref<HTMLInputElement>();
const breakpoint = useBreakpoint();
const {error} = useFormField(toRef(() => props.modelValue), toRef(() => props.rules), dependencyField, props.isOptional);

const isPlaceholder = computed(() => {
  const placeholderValues = ['下限なし', '上限なし', '未選択', '------', props.placeholder, '', undefined];
  return placeholderValues.includes(selectedText.value);
});

const selected = computed(() => {
  if (props.returnObject) {
    const modelValue = props.modelValue as { [key: string]: unknown };
    return props.items.find((i) => i[props.itemValue] === modelValue[props.itemValue]);
  }
  return props.items.find((i) => i[props.itemValue] === props.modelValue);
});
const selectedText = computed(() => (selected.value ? selected.value[props.itemText] : ''));

const selectedItems = computed(() =>
  selected.value ? props.items.find(item => selected.value && item[props.itemValue] === selected.value[props.itemValue]) : ''
);

function onClick() {
  openMenu.value = !openMenu.value;
}

function onSelect(item: { [key: string]: unknown } | undefined) {
  if (props.disabled) return;
  if (item) {
    if (props.returnObject) {
      emit('update:modelValue', item);
    } else {
      const value = item[props.itemValue];
      emit('update:modelValue', value);
    }
  } else {
    emit('update:modelValue', undefined);
  }

  openMenu.value = false;
}


</script>

<style scoped>
.input-container {
  height: 50px;
  width: 100%;
  font-size: 16px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 5px;
  background-color: var(--grayLight) !important;
  transition: background 300ms;
  display: flex;
  align-items: center;
  align-content: center;
  cursor: pointer;
}

.input-container + .error {
  border-radius: 60px;
  border: 2px solid var(--error);
}

input {
  font-size: 16px;
  border: none;
  background-color: transparent;
  transition: background 300ms;
  cursor: pointer;
}

.placeholder-style {
  color: var(--black) !important;
}

::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: var(--black);
  opacity: 1; /* Firefox */
}

:-ms-input-placeholder { /* Internet Explorer 10-11 */
  color: red;
}

::-ms-input-placeholder { /* Microsoft Edge */
  color: red;
}


.menu-content {
  background-color: white;
  border: 2px var(--black) solid;
  contain: content;
}

.menu-title {
  height: 65px;
  padding: 20px 12px;
  font-size: 14px;
}

.menu-list {
  max-height: 300px;
  overflow-y: auto;
}

.menu-item {
  min-height: 48px;
  cursor: pointer;
  transition: 300ms;
}

.menu-item:hover {
  background-color: var(--gray);
}

input {
  width: 100%;
}

.label-border {
  border-bottom: 1px solid var(--primary);
}

</style>
